/**
 * 即給API（Sokyu）エラーメッセージのパーサー
 *
 * 即給APIのエラーは以下のような形式でバックエンドからそのまま返ってくる:
 *   "登録時チェックエラーがありました。詳細よりエラー内容をご確認していただき、修正後に再度で登録を行なってください。
 *    金融機関コード・金融機関支店コード：マスタに存在しない値です。"
 *
 * このメッセージから「<項目名>：<理由>」の部分を抽出し、
 * 口座情報フォームの各入力項目（bankCode / bankBranchCode / depositType / accountNo / accountName）
 * へ振り分けたわかりやすいエラーメッセージに変換する。
 *
 * 項目名が店舗・企業側の即給設定（事業所番号など）を指す場合はワーカーが対処できないため、
 * 原文を出さずに問い合わせ案内（storeConfigMessage）へ振り替える。
 */

export type BankAccountFieldKey =
  | 'bankCode'
  | 'bankBranchCode'
  | 'depositType'
  | 'accountNo'
  | 'accountName';

export type BankAccountFieldErrors = Partial<Record<BankAccountFieldKey, string>>;

/** 即給API側の項目名 → フロントのフィールドキー */
const SOKYU_FIELD_TO_FORM_KEY: Record<string, BankAccountFieldKey> = {
  金融機関コード: 'bankCode',
  金融機関支店コード: 'bankBranchCode',
  預金種目: 'depositType',
  預金種別: 'depositType',
  口座番号: 'accountNo',
  口座名義: 'accountName',
};

/**
 * 店舗・企業側の即給設定に起因する項目。ワーカーには直せないため、口座欄の赤枠ではなく
 * 「店舗担当者へ問い合わせ」の案内に振り替える。
 * （「事業所番号」は仕様書上の表記。実環境では「事業者番号」と返るケースがあるため両方拾う）
 */
const STORE_CONFIG_FIELDS = [
  '事業所番号',
  '事業者番号',
  '企業番号',
  '企業ID',
  '給与支払サイクル番号',
  '利用上限率',
  '一日の利用限度額',
];

const STORE_CONFIG_MESSAGE =
  '店舗の即払い設定に不備があるため、現在この操作を完了できません。お手数ですが店舗担当者へお問い合わせください。';

/** フィールドキー → ユーザー向け表示ラベル */
const FIELD_LABEL: Record<BankAccountFieldKey, string> = {
  bankCode: '銀行コード',
  bankBranchCode: '支店コード',
  depositType: '預金種別',
  accountNo: '口座番号',
  accountName: '口座名義',
};

/** エラー理由 → 各項目用のわかりやすいメッセージへ変換 */
const buildFieldMessage = (label: string, reason: string): string => {
  const r = reason.trim();
  if (r.includes('マスタに存在しない')) {
    return `${label}に誤りがあります`;
  }
  if (r.includes('必須項目')) {
    return `${label}を入力してください`;
  }
  if (r.includes('入力された文字の形式が正しくない') || r.includes('桁数に誤り')) {
    return `${label}の形式または桁数に誤りがあります`;
  }
  // 既知パターン以外はラベル＋理由をそのまま表示
  return `${label}：${r}`;
};

export interface ParsedSokyuError {
  /** 項目別エラー（フォーム各入力欄に表示する） */
  fieldErrors: BankAccountFieldErrors;
  /** どの項目にも振り分けられなかった残りのメッセージ（上部 Alert 表示用、空文字なら不要） */
  remainingMessage: string;
  /** 店舗側の即給設定不備が原因のとき、ワーカー向けの案内文（該当なしなら undefined） */
  storeConfigMessage?: string;
}

/**
 * 即給エラーメッセージを解析して、フィールド別エラーに振り分ける。
 * 振り分けが1件もできなかった場合は null を返す（呼び出し側で元のメッセージを使う）。
 */
export const parseSokyuBankAccountError = (
  message: string | undefined | null
): ParsedSokyuError | null => {
  if (!message) return null;

  const fieldErrors: BankAccountFieldErrors = {};
  const unmatchedSegments: string[] = [];
  let storeConfigMessage: string | undefined;

  // 「<項目名（・区切りで複数可）>：<理由>」のパターンを抽出する。
  // 全角コロン「：」と半角コロン「:」の両方に対応。
  // 区切りは行頭・「。」直後を起点とし、「。」までを1つのセグメントとして拾う。
  const pattern = /(?:^|。)\s*([^。：:]+?)\s*[：:]\s*([^。]+)/g;

  let matchedAny = false;
  for (const match of Array.from(message.matchAll(pattern))) {
    const fieldsStr = match[1].trim();
    const reason = match[2].trim().replace(/。+$/, '');

    // 「・」または「･」で複数項目を分割
    const fields = fieldsStr.split(/[・･]/).map((s) => s.trim()).filter(Boolean);

    let segmentMatched = false;
    for (const field of fields) {
      const key = SOKYU_FIELD_TO_FORM_KEY[field];
      if (key) {
        const label = FIELD_LABEL[key];
        fieldErrors[key] = buildFieldMessage(label, reason);
        segmentMatched = true;
        matchedAny = true;
        continue;
      }
      // 店舗側設定の項目は、ワーカーに原文（例「事業所番号：マスタに存在しない値です」）を
      // 見せても対処できないため、問い合わせ案内に振り替える。
      if (STORE_CONFIG_FIELDS.some((name) => field.includes(name))) {
        storeConfigMessage = STORE_CONFIG_MESSAGE;
        segmentMatched = true;
        matchedAny = true;
      }
    }

    if (!segmentMatched) {
      unmatchedSegments.push(`${fieldsStr}：${reason}`);
    }
  }

  if (!matchedAny) {
    return null;
  }

  return {
    fieldErrors,
    remainingMessage: unmatchedSegments.join(' / '),
    storeConfigMessage,
  };
};
